import React, { useState, useEffect } from "react";
import Grid from '@mui/material/Grid';
import {Typography} from '@mui/material';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import styles from '../inputFields/styles.js';
import "animate.css/animate.css";
import './styles/TagDecimalFields.css';

const TagDecimalFielsEditable = (props) => {
  const minNum = props.minNum;
  const maxNum = props.maxNum;
  const [numValue, setNumValue] = useState("");
  const [tipText, setTipText] = useState(props.prompt);
  const [warningMsg, setWarningMsg] = useState('');
  const [error, setError] = useState(false);
  const isAlwaysOn = props.isAlwaysOn || false;
  const validationCase = props.validationCase || 'none';
  const isTagChecked = props.isTagChecked || false;

 useEffect(() => {
    const initialValue = props.value !== undefined && props.value !== null ? String(props.value) : "";
    let processedValue = initialValue;

    if (initialValue !== "") {
        let num = parseFloat(initialValue);
        if (!isNaN(num)) {
            if (minNum !== undefined && num < minNum) num = minNum;
            if (maxNum !== undefined && num > maxNum) num = maxNum;
            processedValue = String(num);
        } else {
             processedValue = "";
        }
    }
    setNumValue(processedValue);

    let newTipText = props.prompt || '';
    if (minNum !== undefined && maxNum !== undefined) {
      newTipText += ` Number must be between ${minNum} and ${maxNum}.`;
    } else if (minNum !== undefined) {
      newTipText += ` Number must be greater than or equal to ${minNum}.`;
    } else if (maxNum !== undefined) {
      newTipText += ` Number must be less than or equal to ${maxNum}.`;
    }
    setTipText(newTipText.trim());

  }, [props.value, minNum, maxNum, props.prompt]);

  useEffect(() => {
    if (numValue !== "") {
      const num = parseFloat(numValue);
      if (!isNaN(num)) {
        if (maxNum !== undefined && num > maxNum) setWarningMsg(`Number must be less than or equal to ${maxNum}`);
        else if (minNum !== undefined && num < minNum) setWarningMsg(`Number must be greater than or equal to ${minNum}`);
        else setWarningMsg('');
      } else {
         setWarningMsg('Invalid decimal number');
      }
    } else {
      setWarningMsg('');
    }
  }, [numValue, minNum, maxNum]);

  // Enhanced useEffect: Handle both Case 4 and Case 2 validation
  useEffect(() => {
    let newError = false;
    
    if (validationCase === 'case_4') {
      // Case 4: Tag is required (alwaysOn), Variable Value is required
      if (props.required && isAlwaysOn) {
        if (numValue === "" || numValue === null || numValue === undefined) {
          newError = true;
        }
      }
    } else if (validationCase === 'case_2') {
      // Case 2: Tag is not required, but if tag is selected then the Variable is required
      if (props.required && isTagChecked) {
        if (numValue === "" || numValue === null || numValue === undefined) {
          newError = true;
        }
      }
    }
    
    setError(newError);
  }, [numValue, props.required, isAlwaysOn, validationCase, isTagChecked]);

  const handleChange = (event) => {
    const currentVal = event.target.value;
    setNumValue(currentVal);

    let finalValueToPropagate;
    let isValid = true;

    if (currentVal.trim() === "") {
      finalValueToPropagate = "";
      
      // Check validity based on validation case
      if (validationCase === 'case_4' && props.required) {
        isValid = false;
      } else if (validationCase === 'case_2' && props.required && isTagChecked) {
        isValid = false;
      }
    } else {
      const num = parseFloat(currentVal);
      if (isNaN(num) && currentVal !== "-" && !currentVal.endsWith(".")) {
        finalValueToPropagate = currentVal;
        isValid = false;
      } else {
        finalValueToPropagate = isNaN(num) ? currentVal : num;
      }
    }

    props.onChange({
      name: props.param,
      value: finalValueToPropagate,
      isValid: isValid
    });
  };

  const handleBlur = (event) => {
    let currentVal = event.target.value;
    let finalNum;
    let finalNumStr;

    if (currentVal.trim() === "") {
      if (props.required && (validationCase === 'case_4' || (validationCase === 'case_2' && isTagChecked))) {
        finalNumStr = "";
        finalNum = undefined;
      } else {
        finalNum = 0.0;
        finalNumStr = "0.00";
      }
    } else {
      finalNum = parseFloat(currentVal);
      if (isNaN(finalNum)) {
        finalNumStr = (props.required && (validationCase === 'case_4' || (validationCase === 'case_2' && isTagChecked))) ? "" : "0.00";
        finalNum = parseFloat(finalNumStr);
      } else {
        if (minNum !== undefined && finalNum < minNum) finalNum = minNum;
        if (maxNum !== undefined && finalNum > maxNum) finalNum = maxNum;
        finalNumStr = finalNum.toFixed(2);
      }
    }
    setNumValue(finalNumStr);

    let isValid = true;
    if (validationCase === 'case_4' && props.required && finalNumStr.trim() === "") {
      isValid = false;
    } else if (validationCase === 'case_2' && props.required && isTagChecked && finalNumStr.trim() === "") {
      isValid = false;
    }
    if (props.required && isNaN(finalNum) && finalNumStr !== "") isValid = false;

    props.onChange({
      name: props.param,
      value: finalNum,
      isValid: isValid
    });
  };

  // Determine helper text based on validation case
  const getHelperText = () => {
    if (error) {
      if (validationCase === 'case_4') {
        return "This field is required";
      } else if (validationCase === 'case_2') {
        return "This field is required if active";
      }
    }
    return warningMsg || "";
  };

  return (
    <Grid container className="tag-decimal-field-editable-container">
      <Grid item xs={11} sm={11} className="text-field-container">
        <Tooltip title={tipText} placement="top" arrow>
          <TextField
            className="decimal-text-field"
            label={props.label}
            size="small"
            type="text"
            value={numValue}
            onChange={handleChange}
            onBlur={handleBlur}
            error={error} // Show red highlight for both Case 2 and Case 4
            helperText={getHelperText()} // Different message based on case
            InputProps={{
              endAdornment: null // Remove clear button
            }}
            variant={props.variant || "outlined"}
            style={styles.formControlTextfield}
            inputProps={{
              className: "decimal-text-field-input"
            }}
          />
        </Tooltip>
      </Grid>
    </Grid>
  );
};

export default TagDecimalFielsEditable;